import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import axios from 'axios';
import {
  devAutoLogin,
  getMe,
  login as loginApi,
  logout as logoutApi,
  refreshPermissions,
} from '@/api/generated/auth/auth';
import type { MeResponse } from '@/api/generated/schemas/meResponse';
import { axiosInstance } from '@/api/customInstance';
import { AuthActionsContext, AuthContext } from '@/shared/auth/AuthContext';
import { useOttExchange } from '@/shared/auth/useOttExchange';
import { permissionsFromApi } from '@/shared/auth/permissionsFromApi';
import { isPhoenixOnTopMode } from '@/shared/legacyMagicBridge';
import type { AuthActions, AuthState, AuthUser } from '@/shared/auth/types';

interface AuthProviderProps {
  children: ReactNode;
}

function toAuthUser(me: MeResponse): AuthUser {
  return {
    userId: me.userId,
    userName: me.userName,
    permissions: permissionsFromApi(me.permissions),
    isConfig: me.isConfig,
  };
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [authState, setAuthState] = useState<AuthState>({ status: 'loading' });
  const { exchange: exchangeOtt } = useOttExchange();

  const authenticate = useCallback(async () => {
    // Step 1: check if already authenticated
    try {
      const me = await getMe();
      setAuthState({ status: 'authenticated', user: toAuthUser(me) });
      return;
    } catch (e) {
      if (!axios.isAxiosError(e) || e.response?.status !== 401) {
        setAuthState({ status: 'error', message: 'Server unavailable' });
        return;
      }
    }

    // Step 2a: dev auto-login (only in dev, before iframe check)
    if (import.meta.env.VITE_DEV_AUTO_LOGIN === 'true') {
      try {
        await devAutoLogin();
        const me = await getMe();
        setAuthState({ status: 'authenticated', user: toAuthUser(me) });
        return;
      } catch {
        console.warn('[DEV] Dev auto-login failed, falling back to OTT flow');
      }
    }

    // Step 2b: not authenticated.
    // - Phoenix-On-Top mode: Phoenix je autoritou přihlášení → zobrazíme vlastní LoginGate (uživatel zadá
    //   jméno/heslo přes akci `login` níže). Žádné čekání na push z Magicu.
    // - Normal mode: Phoenix je v iframu Magicu → pullneme OTT z window.top a vyměníme za session.
    if (isPhoenixOnTopMode()) {
      setAuthState({ status: 'unauthenticated' });
      return;
    }

    try {
      if (window.self === window.top) {
        setAuthState({ status: 'error', message: 'Authentication requires legacy context' });
        return;
      }
      await exchangeOtt();
      const me = await getMe();
      setAuthState({ status: 'authenticated', user: toAuthUser(me) });
    } catch {
      setAuthState({ status: 'error', message: 'Authentication failed' });
    }
  }, [exchangeOtt]);

  useEffect(() => {
    void authenticate();
  }, [authenticate]);

  // Akce pro LoginGate / UserAccountMenu.
  const login = useCallback(
    async (userName: string, password: string) => {
      // Při chybě (neplatné údaje / 2FA / neaktivní) hodí AxiosError – LoginGate ji zobrazí.
      await loginApi({ userName, password });
      await authenticate();
    },
    [authenticate]
  );

  const signOut = useCallback(async () => {
    try {
      await logoutApi();
    } catch {
      // best-effort: i kdyby logout endpoint selhal, přepneme na LoginGate.
    }
    setAuthState({ status: 'unauthenticated' });
  }, []);

  const actions = useMemo<AuthActions>(
    () => ({ login, signOut, reauthenticate: authenticate }),
    [login, signOut, authenticate]
  );

  // Transparent re-auth on 401, and permission refresh + retry on 403
  useEffect(() => {
    const id = axiosInstance.interceptors.response.use(undefined, async (error: unknown) => {
      if (!axios.isAxiosError(error)) return Promise.reject(error);

      const status = error.response?.status;
      const url = error.config?.url ?? '';
      const isAuthEndpoint = url.includes('/api/auth/');

      if (status === 401 && !isAuthEndpoint) {
        await authenticate();
        return Promise.reject(error);
      }

      if (
        status === 403 &&
        !isAuthEndpoint &&
        !error.config?.headers?.['X-Permissions-Refreshed']
      ) {
        await refreshPermissions();
        const retryConfig = {
          ...error.config,
          headers: { ...error.config?.headers, 'X-Permissions-Refreshed': '1' },
        };
        return axiosInstance.request(retryConfig);
      }

      return Promise.reject(error);
    });
    return () => axiosInstance.interceptors.response.eject(id);
  }, [authenticate]);

  return (
    <AuthContext value={authState}>
      <AuthActionsContext value={actions}>{children}</AuthActionsContext>
    </AuthContext>
  );
}
